import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Toasts da aplicação, sobre o `Flashbar` do Cloudscape.
 *
 * O `Flashbar` é um componente controlado: quem guarda a lista é este
 * contexto. A API pública dos toasts (`useAppToast`) permanece igual à do
 * LeafyGreen (`success/error/warning/info/loading/dismiss`) para não tocar os
 * ~60 arquivos que já usam.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'in-progress';

export interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  /** `true` para toasts de carregamento: não expiram sozinhos. */
  loading?: boolean;
  /** Tempo de vida em ms. `null` = não expira. */
  timeout: number | null;
}

interface ToastContextValue {
  items: ToastItem[];
  push: (toast: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue>({
  items: [],
  push: () => '',
  dismiss: () => undefined,
  dismissAll: () => undefined,
});

let counter = 0;
const nextId = () => `toast-${++counter}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current.clear();
    setItems([]);
  }, []);

  const push = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = nextId();
    setItems((current) => [...current, { ...toast, id }]);
    if (toast.timeout !== null && toast.timeout !== undefined) {
      const timer = setTimeout(() => dismiss(id), toast.timeout);
      timers.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({ items, push, dismiss, dismissAll }), [items, push, dismiss, dismissAll]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export const useToastContext = () => useContext(ToastContext);
